import { z } from "zod/v4";

import {
  BundleConfigDescriptions,
  BundleCopyDescriptions,
  ComponentDtoDescriptions,
  ComponentPlacementDescriptions,
  OnsiteUpsellConfigDescriptions,
  OnsiteUpsellRuleDescriptions,
} from "../descriptions/component-dto.describe";
import { assertDescriptions } from "../descriptions/types";

import { BundleDiscountSchema } from "./bundle-discount";
import { type InsertType, InsertTypeList } from "./onsite-edits";
import { RuleTargetingSchema } from "./targeting";
import {
  UpsellDiscountSchema,
  UpsellProductRulesNode,
} from "./upsell-product-rules";

export const ComponentConfigTypeList = [
  "onsiteUpsell",
  "bundleOnsiteUpsell",
] as const;
export type ComponentConfigType = (typeof ComponentConfigTypeList)[number];

export const ComponentPlacementLocationList = ["pdp", "cart"] as const;

export type ComponentPlacementLocation =
  (typeof ComponentPlacementLocationList)[number];

export const OnsiteUpsellNamedSlotList = ["atc", "variant-select"] as const;

export const OnsiteUpsellProductSlotList = [
  "product.image",
  "product.url",
  "product.title",
  "product.price",
  "product.compareAtPrice",
] as const;

export type OnsiteUpsellSlot =
  | (typeof OnsiteUpsellProductSlotList)[number]
  | (typeof OnsiteUpsellNamedSlotList)[number];

// Slots outside the per-product block, plus the per-product `checkbox`.
export const BundleNamedSlotList = [
  "heading",
  "subheading",
  "checkbox",
  "bundle.price",
  "bundle.comparePrice",
  "bundle.atc",
] as const;

// No `atc`: a bundle has one add-to-cart, from `bundle.atc`.
export type BundleSlot =
  | (typeof OnsiteUpsellProductSlotList)[number]
  | (typeof BundleNamedSlotList)[number]
  | "variant-select";

export type ComponentTemplateSlot = OnsiteUpsellSlot | BundleSlot;

/**
 * Anchor keys reach the storefront as a CSS attribute selector
 * (`[data-ig-anchor="<key>"]`) and as a Liquid setting a merchant types by hand.
 * The charset is deliberately narrow so neither context needs escaping to be safe.
 */
export const ANCHOR_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * Where a component mounts. Two mutually exclusive modes:
 *
 * - **anchor** (`anchorKey`): the merchant drops the Intelligems Content
 *   theme app block wherever they want and gives it this key. The block owns the
 *   position; the component renders inside it.
 * - **selector** (`selector` + `insertType`): the legacy mode, where the position
 *   is a CSS selector picked in the Experience Builder.
 */
export const ComponentPlacementSchema = z
  .object({
    selector: z
      .string()
      .optional()
      .describe(ComponentPlacementDescriptions.selector),
    insertType: z
      .enum(InsertTypeList)
      .optional()
      .describe(ComponentPlacementDescriptions.insertType),
    anchorKey: z
      .string()
      .regex(ANCHOR_KEY_PATTERN)
      .optional()
      .describe(ComponentPlacementDescriptions.anchorKey),
    location: z
      .enum(ComponentPlacementLocationList)
      .describe(ComponentPlacementDescriptions.location),
  })
  .refine(
    (placement) =>
      placement.anchorKey
        ? !placement.selector && !placement.insertType
        : !!placement.selector && !!placement.insertType,
    {
      error:
        "Placement must set either anchorKey or selector+insertType, not both.",
    }
  );

export type ComponentPlacement = z.infer<typeof ComponentPlacementSchema>;

export const OnsiteUpsellRuleSchema = z.object({
  id: z.string().describe(OnsiteUpsellRuleDescriptions.id),
  priority: z.number().int().describe(OnsiteUpsellRuleDescriptions.priority),
  action: z
    .array(UpsellProductRulesNode)
    .describe(OnsiteUpsellRuleDescriptions.action),
  targeting: RuleTargetingSchema.describe(
    OnsiteUpsellRuleDescriptions.targeting
  ),
});

export type OnsiteUpsellRule = z.infer<typeof OnsiteUpsellRuleSchema>;

export const OnsiteUpsellConfigSchema = z.object({
  type: z.literal("onsiteUpsell").describe(OnsiteUpsellConfigDescriptions.type),
  placement: ComponentPlacementSchema.describe(
    OnsiteUpsellConfigDescriptions.placement
  ),
  template: z.string().describe(OnsiteUpsellConfigDescriptions.template),
  maxProductLimit: z
    .number()
    .int()
    .describe(OnsiteUpsellConfigDescriptions.maxProductLimit),
  excludeOutOfStock: z
    .boolean()
    .default(true)
    .describe(OnsiteUpsellConfigDescriptions.excludeOutOfStock),
  excludeItemsInCart: z
    .boolean()
    .default(true)
    .describe(OnsiteUpsellConfigDescriptions.excludeItemsInCart),
  excludePdpProduct: z
    .boolean()
    .default(true)
    .describe(OnsiteUpsellConfigDescriptions.excludePdpProduct),
  discount: UpsellDiscountSchema.optional().describe(
    OnsiteUpsellConfigDescriptions.discount
  ),
  rules: z
    .array(OnsiteUpsellRuleSchema)
    .describe(OnsiteUpsellConfigDescriptions.rules),
  agenticLogicPlacementId: z
    .uuid()
    .nullish()
    .describe(OnsiteUpsellConfigDescriptions.agenticLogicPlacementId),
  // Write-side intent and read-side echo, never stored in the config blob:
  // the assignment row owns which rule serves a placement, so a copy inside
  // the JSON would be a second truth free to drift. The controller strips it
  // before persisting and the response derives it again on every read.
  //
  // The empty-result policy is NOT here at all any more: it is a rule setting,
  // and the container neither stores nor echoes it.
  agenticLogicRuleId: z
    .uuid()
    .nullish()
    .describe(OnsiteUpsellConfigDescriptions.agenticLogicRuleId),
});

export type OnsiteUpsellConfig = z.infer<typeof OnsiteUpsellConfigSchema>;

export const BundleCopySchema = z.object({
  heading: z.string().describe(BundleCopyDescriptions.heading),
  subheading: z.string().optional().describe(BundleCopyDescriptions.subheading),
  buttonText: z.string().describe(BundleCopyDescriptions.buttonText),
  rowBadgeLabel: z
    .string()
    .optional()
    .describe(BundleCopyDescriptions.rowBadgeLabel),
  savedLabel: z.string().optional().describe(BundleCopyDescriptions.savedLabel),
  sublineToNextTier: z
    .string()
    .optional()
    .describe(BundleCopyDescriptions.sublineToNextTier),
  sublineAtTopTier: z
    .string()
    .optional()
    .describe(BundleCopyDescriptions.sublineAtTopTier),
});
export type BundleCopy = z.infer<typeof BundleCopySchema>;

export const BundleConfigSchema = z.object({
  type: z.literal("bundleOnsiteUpsell").describe(BundleConfigDescriptions.type),
  placement: ComponentPlacementSchema.describe(
    BundleConfigDescriptions.placement
  ),
  template: z.string().describe(BundleConfigDescriptions.template),
  copy: BundleCopySchema.describe(BundleConfigDescriptions.copy),
  maxProductLimit: z
    .number()
    .int()
    .describe(BundleConfigDescriptions.maxProductLimit),
  allowItemSelection: z
    .boolean()
    .default(true)
    .describe(BundleConfigDescriptions.allowItemSelection),
  excludeOutOfStock: z
    .boolean()
    .default(true)
    .describe(BundleConfigDescriptions.excludeOutOfStock),
  excludeItemsInCart: z
    .boolean()
    .default(true)
    .describe(BundleConfigDescriptions.excludeItemsInCart),
  excludePdpProduct: z
    .boolean()
    .default(true)
    .describe(BundleConfigDescriptions.excludePdpProduct),
  discount: BundleDiscountSchema.optional().describe(
    BundleConfigDescriptions.discount
  ),
  rules: z
    .array(OnsiteUpsellRuleSchema)
    .describe(BundleConfigDescriptions.rules),
  agenticLogicPlacementId: z
    .uuid()
    .nullish()
    .describe(BundleConfigDescriptions.agenticLogicPlacementId),
  agenticLogicRuleId: z
    .uuid()
    .nullish()
    .describe(BundleConfigDescriptions.agenticLogicRuleId),
});
export type BundleConfig = z.infer<typeof BundleConfigSchema>;

export const OnsiteUpsellAnalyticsEventContextSchema = z.object({
  componentId: z.string(),
  variationId: z.string(),
  componentType: z.enum(ComponentConfigTypeList),
  location: ComponentPlacementSchema.shape.location,
  ruleId: z.string(),
  invocationId: z.string().optional(),
  sessionId: z.string().optional(),
  pageViewId: z.string().optional(),
  // Present only for anchor-mode placements, where one component can render
  // into several theme blocks on the same page. It is Shopify's per-block id,
  // and it is what tells those instances apart in reporting.
  blockId: z.string().optional(),
});

export type OnsiteUpsellAnalyticsEventContext = z.infer<
  typeof OnsiteUpsellAnalyticsEventContextSchema
>;

export const ComponentConfigSchema = z.discriminatedUnion("type", [
  OnsiteUpsellConfigSchema,
  BundleConfigSchema,
]);

export type ComponentConfig = z.infer<typeof ComponentConfigSchema>;

export const ComponentDtoSchema = z.object({
  id: z.uuid().describe(ComponentDtoDescriptions.id),
  variationId: z.uuid().describe(ComponentDtoDescriptions.variationId),
  config: ComponentConfigSchema.describe(ComponentDtoDescriptions.config),
});

export type ComponentDto = z.infer<typeof ComponentDtoSchema>;

export type OnsiteUpsellComponentDto = Omit<ComponentDto, "config"> & {
  config: OnsiteUpsellConfig;
};

export type BundleComponentDto = Omit<ComponentDto, "config"> & {
  config: BundleConfig;
};

export const CreateComponentDtoSchema = ComponentDtoSchema.extend({
  id: z.uuid().optional(),
});

export type CreateComponentDto = z.infer<typeof CreateComponentDtoSchema>;

// Compile-time checks: description objects must cover all schema keys
assertDescriptions<typeof ComponentPlacementDescriptions, ComponentPlacement>();
assertDescriptions<typeof OnsiteUpsellRuleDescriptions, OnsiteUpsellRule>();
assertDescriptions<typeof OnsiteUpsellConfigDescriptions, OnsiteUpsellConfig>();
assertDescriptions<typeof BundleCopyDescriptions, BundleCopy>();
assertDescriptions<typeof BundleConfigDescriptions, BundleConfig>();
assertDescriptions<typeof ComponentDtoDescriptions, ComponentDto>();
